import { apiService } from "./api.service";
import {
  tokenStorage,
  appLocalStorage,
  appSessionStorage,
} from "utils/storage";
import { STORAGE_KEYS } from "constants/index";
import { LoginCredentials, AuthResponse } from "types/index";
import { User } from "types/user";
import { Company } from "@/types/company";
import { Employee } from "@/types/employee";
import { access } from "fs";

class AuthService {
  private decodeJwtPayload(token: string): Record<string, any> | null {
    try {
      const parts = token.split(".");
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );
      const json = atob(padded);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /**
   * Get the expiration time from a JWT token
   */
  getTokenExpiration(token: string | null): number | null {
    if (!token) return null;

    const payload = this.decodeJwtPayload(token);
    if (!payload || !payload.exp) return null;

    // exp is in seconds, convert to milliseconds
    return payload.exp * 1000;
  }

  /**
   * Check if the token is expired
   */
  isTokenExpired(token: string | null): boolean {
    const expiration = this.getTokenExpiration(token);
    if (!expiration) return true;

    // Add a small buffer (5 seconds) to handle clock skew
    return Date.now() >= expiration - 5000;
  }

  /**
   * Get the time remaining until token expiration in milliseconds
   */
  getTimeUntilExpiration(token: string | null): number {
    const expiration = this.getTokenExpiration(token);
    if (!expiration) return 0;

    return Math.max(0, expiration - Date.now());
  }

  /**
   * Check if the token will expire within the specified number of seconds
   */
  willExpireWithin(token: string | null, seconds: number = 60): boolean {
    const timeUntilExpiration = this.getTimeUntilExpiration(token);
    return timeUntilExpiration > 0 && timeUntilExpiration <= seconds * 1000;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    appLocalStorage.set(STORAGE_KEYS.DOMAIN, "");
    try {
      const response = await apiService.post<AuthResponse>(
        "/auth/signIn",
        credentials
      );

      // Store domain proactively if present in any response shape
      const respAny = response as any;
      const earlyDomain = (respAny?.domain ||
        respAny?.Domain ||
        respAny?.data?.domain ||
        respAny?.data?.Domain) as string | undefined;
      if (earlyDomain) {
        appLocalStorage.set(STORAGE_KEYS.DOMAIN, earlyDomain);
      }

      if (response.success) {
        // Check if access_token exists in the response (either at root or in data)
        // Make sure to check for actual truthy string values, not just existence
        const accessTokenAtRoot = respAny.access_token || respAny.accessToken;
        const accessTokenInData = respAny.data && (respAny.data.access_token || respAny.data.accessToken);
        const hasAccessToken = !!(accessTokenAtRoot && typeof accessTokenAtRoot === 'string' && accessTokenAtRoot.trim() !== "") || 
                              !!(accessTokenInData && typeof accessTokenInData === 'string' && accessTokenInData.trim() !== "");
        
        // If no access_token, this is a registration flow response
        if (!hasAccessToken) {
          // Clear any existing tokens and user data since this is not a full login
          tokenStorage.clearTokens();
          try {
            appLocalStorage.remove(STORAGE_KEYS.USER);
          } catch {}
          
          const user: User = {
            id: String(respAny.id || respAny.user_id || respAny.userId || "me"),
            username: respAny.username || respAny.user_name || credentials.email,
            email: respAny.email || credentials.email,
            name: respAny.user_name || respAny.name || "",
            phone: respAny.user_phone || respAny.phone,
            phoneId: respAny.user_phoneId || respAny.phoneId,
            company_id: respAny.company_id || respAny.companyId ? String(respAny.company_id || respAny.companyId) : "",
            createdAt: new Date().toISOString(),
            isCodeRequired: respAny.is_code_required ?? false,
            isActive: respAny.is_active ?? false,
            redirect_to_register: respAny.redirect_to_register === true,
            step: respAny.step ?? 1,
            package_details_id: respAny.package_details_id,
          } as any;
          // Don't store user in localStorage - it will be stored in sessionStorage by the login page
          var accessToken = "";
          var refreshToken = "";
          return {
            user,
            tokens: {
              accessToken,
              refreshToken,
            },
          };
        }
        if (
          (response as any).data &&
          (response as any).data.user &&
          (response as any).data.tokens
        ) {
          const { user, tokens } = (response as any).data as AuthResponse;
          const companyName = ((response as any).data as any)?.company_name as
            | string
            | undefined;
          const domain = ((response as any).data as any)?.domain as
            | string
            | undefined;
          tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);

          const payload = this.decodeJwtPayload(tokens.accessToken);
          const tokenRoleId = (payload?.roleId ??
            payload?.role_id ??
            payload?.role ??
            undefined) as number | undefined;
          if (typeof tokenRoleId === "number") {
            (user as User).roleId = tokenRoleId;
          }

          this.setUser(user);
          if (domain) appLocalStorage.set(STORAGE_KEYS.DOMAIN, domain);
          return { user, tokens };
        }
        const dataAny = (response as any).data as any;
        if (dataAny && (dataAny.access_token || dataAny.accessToken)) {
          const accessToken = (dataAny.access_token ||
            dataAny.accessToken) as string;
          const refreshToken = (dataAny.refresh_token ||
            dataAny.refreshToken ||
            "") as string;
          const companyName = (dataAny.company_name || dataAny.companyName) as
            | string
            | undefined;
          const company_id = (dataAny.company_id || dataAny.company_id) as
            | string
            | undefined;
          const domain = (dataAny.domain || dataAny.Domain) as
            | string
            | undefined;

          tokenStorage.setTokens(accessToken, refreshToken);

          const payload = this.decodeJwtPayload(accessToken);
          const rawRole = (payload?.roleId ??
            payload?.role_id ??
            payload?.RoleId) as number | string | undefined;
          const tokenRoleId =
            typeof rawRole === "string" ? Number(rawRole) : rawRole;
          const user: User = {
            id: "me",
            username: credentials.email,
            name: companyName || credentials.email,
            company_id: company_id || "",
            roleId: tokenRoleId,
            createdAt: new Date().toISOString(),
          };

          this.setUser(user);
          if (domain) appLocalStorage.set(STORAGE_KEYS.DOMAIN, domain);

          return {
            user,
            tokens: {
              accessToken,
              refreshToken,
            },
          };
        }

        const flatAny = response as any;
        if (flatAny && (flatAny.access_token || flatAny.accessToken)) {
          const accessToken = (flatAny.access_token ||
            flatAny.accessToken) as string;
          const refreshToken = (flatAny.refresh_token ||
            flatAny.refreshToken ||
            "") as string;
          const companyName = (flatAny.company_name || flatAny.companyName) as
            | string
            | undefined;
          const domain = (flatAny.domain || flatAny.Domain) as
            | string
            | undefined;

          tokenStorage.setTokens(accessToken, refreshToken);

          const payload = this.decodeJwtPayload(accessToken);
          const rawRole = (payload?.roleId ??
            payload?.role_id ??
            payload?.RoleId) as number | string | undefined;
          const tokenRoleId =
            typeof rawRole === "string" ? Number(rawRole) : rawRole;
          const company_id = (flatAny.company_id || flatAny.companyId) as
            | string
            | number
            | undefined;
          const user_id = (flatAny.user_id || flatAny.userId || "me") as
            | string
            | number;

          const user: User = {
            id: String(user_id),
            email: flatAny.email || credentials.email,
            username: flatAny.username || credentials.email,
            name: companyName || flatAny.name || credentials.email,
            company_id: company_id ? String(company_id) : "",
            roleId: tokenRoleId,
            createdAt: new Date().toISOString(),
          };

          this.setUser(user);
          if (domain) appLocalStorage.set(STORAGE_KEYS.DOMAIN, domain);

          return {
            user,
            tokens: {
              accessToken,
              refreshToken,
            },
          };
        }
      }

      throw new Error(response.message || "Login failed");
    } catch (error: any) {
      throw error;
    }
  }

  async register(payload: Partial<Company>): Promise<Company> {
    const response = await apiService.post<Company>(`/auth/register`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Company;
    }
    return response as any as Company;
  }

  
  async verifyEmailCode(email: string, code: string): Promise<any> {
    try {
      const response = await apiService.post("/auth/VerifyCode", null,{
        email,
        code,
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  }
  
  
  async resendCode(email: string): Promise<any> {
    try {
      const response = await apiService.resendCode("/auth/ResendCode", {
        email,
      });

      if (!response.success) {
        throw new Error(response.message || "Email verification failed");
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  async registerIndividual(payload: any): Promise<Company> {
    const response = await apiService.post<any>(`/auth/registerindividual`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Company;
    }
    return response as any as Company;
  }

  async logout(): Promise<void> {
    try {
      // Set flag to indicate manual logout (prevents "Token Expired" message)
      try {
        sessionStorage.setItem('__manual_logout__', 'true');
      } catch {}
    } catch (error) {
      console.error("Logout error:", error);
    } 
  }

  getCurrentUser(): User | null {
    return appLocalStorage.get<User>(STORAGE_KEYS.USER);
  }

  setUser(user: User): void {
    appLocalStorage.set(STORAGE_KEYS.USER, user);
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await apiService.put<User>("/auth/profile", data);

      if (response.success && response.data) {
        this.setUser(response.data);
        return response.data;
      }

      throw new Error(response.message || "Profile update failed");
    } catch (error: any) {
      throw error;
    }
  }

  async changePassword(
    id: string,
    newPassword: string
  ): Promise<void> {
    try {
      const response = await apiService.post("/auth/ChangePassword",null,{
        id: id,
        password: newPassword,
      });

      if (!response.success) {
        throw new Error(response.message || "Password change failed");
      }
    } catch (error: any) {
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<any> {
    try {
      const response = await apiService.post("/auth/ForgotPassword",null,{email:email});

      if (!response.success) {
        throw new Error(response.message || "Password reset request failed");
      }
    } catch (error: any) {
      throw error;
    }
  }


  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      const response = await apiService.post("/auth/reset-password", {
        token,
        password: newPassword,
      });

      if (!response.success) {
        throw new Error(response.message || "Password reset failed");
      }
    } catch (error: any) {
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return tokenStorage.hasValidToken() && this.getCurrentUser() !== null;
  }

  clearSession(): void {
    tokenStorage.clearTokens();
    // Preserve non-auth app settings like theme/language; remove only auth keys
    try {
      appLocalStorage.remove(STORAGE_KEYS.USER);
    } catch {}
    try {
      appSessionStorage.clear?.();
    } catch {}
    try {
      localStorage.setItem("__auth_event__", `logout:${Date.now()}`);
    } catch {}
  }

  async refreshToken(): Promise<string> {
    try {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await apiService.post<{ accessToken: string }>(
        "/auth/refresh",
        {
          refreshToken,
        }
      );

      if (response.success && response.data) {
        const { accessToken } = response.data;
        tokenStorage.setTokens(accessToken, refreshToken);
        return accessToken;
      }

      throw new Error("Token refresh failed");
    } catch (error: any) {
      this.clearSession();
      throw error;
    }
  }
}

export const authService = new AuthService();
export default authService;
